import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/Base/Dialog";
import { Button } from "@/components/Base/Button";
import { Badge } from "@/components/Base/Badge";
import { UploadIcon, SparklesIcon, AlertTriangleIcon } from "lucide-react";
import { Automation } from "@/types/v2/Automation";
import { useDynamicOptions } from "@/hooks/useDynamicOptions";
import {
  ImportPlan,
  ParsedAutomationExportFile,
  parseAutomationImportFile,
  extractJsonPayload,
  planAutomationImport,
  applyReferenceMappings,
  collectUnresolvedReferences,
  ReferenceMappings,
} from "@/lib/automationImport";
import { buildAutomationPrompt } from "@/lib/automationPromptGenerator";
import { findChainingIssues } from "@/lib/automationChaining";
import { AutomationRepository } from "@/repositories/automation";
import { showErrorToast, showSuccessToast } from "@/lib/toast";
import { Select } from "@/components/Common/Select";
import { useStore } from "@/stores/useStore";
import { useAutomationBuilderAnalytics } from "@/hooks/useAutomationBuilderAnalytics";

interface ImportAutomationsDialogProps {
  shopId: string;
  automations: Automation[];
}

const DYNAMIC_TYPE_LABELS: Record<string, string> = {
  containers: "Container",
  technicians: "Technician",
  serviceAdvisors: "Service Advisor",
  colors: "Color",
};

export const ImportAutomationsDialog = ({
  shopId,
  automations,
}: ImportAutomationsDialogProps) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<"upload" | "preview">("upload");
  const [parseError, setParseError] = useState<string | null>(null);
  const [parsedFile, setParsedFile] =
    useState<ParsedAutomationExportFile | null>(null);
  const [mode, setMode] = useState<"add" | "replaceAll">("add");
  const [saving, setSaving] = useState(false);
  const [mappings, setMappings] = useState<ReferenceMappings>({});
  const [pastedText, setPastedText] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { generateOptions } = useDynamicOptions();
  const { shop, employeesLoaded } = useStore();
  const { logImportCompleted, logPromptCopied } =
    useAutomationBuilderAnalytics();
  // Options come from useStore()/useServiceAdvisors() via generateOptions.
  // Before the store hydrates they are empty, which would make EVERY reference
  // look missing and every picker empty. Gate the mapping UI + confirm on
  // employeesLoaded so we wait for the first employees snapshot, not just
  // shop presence (employees itself starts as [] and is never undefined).
  const optionsReady = !!shop && employeesLoaded;

  // Distinct missing entities to resolve — derived from the ORIGINAL file so
  // the rows stay stable as the user fills them in.
  const unresolved = parsedFile
    ? collectUnresolvedReferences(parsedFile.automations, generateOptions)
    : [];

  // Cheap, pure, recomputed every render: substitute mappings, then plan.
  const remapped = parsedFile
    ? applyReferenceMappings(parsedFile.automations, mappings, generateOptions)
    : null;
  const plan: ImportPlan | null = remapped
    ? planAutomationImport(remapped, automations, mode, generateOptions)
    : null;

  // Non-blocking: chains the import validator accepts but that won't behave
  // (an action sub-entry without its parent, or a parent missing a required
  // sub-action). Keyed on templateIds, so remapping doesn't affect it.
  const chainingIssues = parsedFile
    ? findChainingIssues(parsedFile.automations)
    : [];

  // Progress is measured in references (occurrences), not distinct entities:
  // mapping one entity resolves all of its uses across the file at once.
  const totalReferences = unresolved.reduce((sum, e) => sum + e.count, 0);
  const mappedReferences = unresolved.reduce(
    (sum, e) => sum + (mappings[e.key] ? e.count : 0),
    0
  );
  const skippedCount =
    plan?.actions.filter((a) => a.kind === "reject").length ?? 0;

  const writeCount =
    plan?.actions.filter((a) => a.kind !== "reject").length ?? 0;

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setStep("upload");
      setParseError(null);
      setParsedFile(null);
      setMode("add");
      setMappings({});
      setPastedText("");
    }
  };

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(buildAutomationPrompt());
      showSuccessToast("Prompt copied", "Paste it into your AI assistant.");
      logPromptCopied();
    } catch (error) {
      console.error(error);
      showErrorToast("Could not copy the prompt to your clipboard");
    }
  };

  const handleRaw = (raw: string) => {
    const result = parseAutomationImportFile(raw);
    if ("error" in result) {
      setParseError(result.error);
      return;
    }
    setParseError(null);
    setParsedFile(result.file);
    setStep("preview");
  };

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      handleRaw(await file.text());
    } catch (error) {
      console.error(error);
      setParseError("Could not read the selected file.");
    }
  };

  const handleConfirm = async () => {
    if (!plan) return;
    setSaving(true);
    try {
      await AutomationRepository.applyImportPlan(
        shopId,
        plan,
        automations.length
      );
      const created = plan.actions.filter((a) => a.kind === "create").length;
      const updated = plan.actions.filter((a) => a.kind === "update").length;
      const skipped = plan.actions.filter((a) => a.kind === "reject").length;
      showSuccessToast(
        `Import complete: ${created} created, ${updated} updated, ${skipped} skipped`
      );
      logImportCompleted(mode, created + updated);
      handleOpenChange(false);
    } catch (error) {
      // Dialog stays open on the preview step so the parsed file isn't lost.
      console.error(error);
      showErrorToast("Failed to import automations");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant={"outline"}>
          <UploadIcon className="mr-2 h-4 w-4" />
          Import
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Import Automations</DialogTitle>
        </DialogHeader>
        {step === "upload" && (
          <>
            <DialogDescription>
              Generate automations with AI, or import an export file.
            </DialogDescription>
            <div className="flex flex-col gap-2 rounded-md border p-3">
              <p className="text-sm font-medium">Generate with AI</p>
              <ol className="list-decimal space-y-1 pl-5 text-xs text-muted-foreground">
                <li>
                  Copy the prompt and paste it into an AI assistant (ChatGPT,
                  Claude, etc.).
                </li>
                <li>
                  Describe the automations you want — it replies with a JSON
                  file.
                </li>
                <li>
                  Paste that JSON below (or upload a file), then map any
                  shop-specific values and import.
                </li>
              </ol>
              <Button
                variant={"outline"}
                className="self-start"
                onClick={handleCopyPrompt}
              >
                <SparklesIcon className="mr-2 h-4 w-4" />
                Copy Prompt
              </Button>
            </div>
            <textarea
              className="min-h-[8rem] w-full rounded-md border bg-transparent p-2 font-mono text-xs"
              placeholder="Paste the JSON your AI assistant produced here"
              value={pastedText}
              onChange={(e) => setPastedText(e.target.value)}
            />
            <Button
              variant={"outline"}
              disabled={pastedText.trim().length === 0}
              onClick={() => handleRaw(extractJsonPayload(pastedText))}
            >
              Load pasted JSON
            </Button>
            <div className="flex items-center gap-2">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs text-muted-foreground">
                or upload a file
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>
            <div
              className="flex flex-col items-center gap-3 rounded-md border border-dashed p-8"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                handleFile(e.dataTransfer.files[0]);
              }}
            >
              <p className="text-sm text-muted-foreground">
                Drag and drop an export file here, or
              </p>
              <Button
                variant={"outline"}
                onClick={() => fileInputRef.current?.click()}
              >
                Choose file
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                className="hidden"
                onChange={(e) => {
                  handleFile(e.target.files?.[0]);
                  // Reset so the same file can be selected again after a fix.
                  e.target.value = "";
                }}
              />
            </div>
            {parseError && (
              <p className="text-sm text-destructive">{parseError}</p>
            )}
          </>
        )}
        {step === "preview" && plan && (
          <>
            {chainingIssues.length > 0 && (
              <div className="flex flex-col gap-2 rounded-md border p-3">
                <div className="flex items-center gap-2">
                  <AlertTriangleIcon className="h-4 w-4 shrink-0 text-amber" />
                  <p className="text-sm font-medium">
                    These automations may not work as expected
                  </p>
                </div>
                <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
                  {chainingIssues.map((issue, i) => (
                    <li key={i}>
                      <span className="font-medium">
                        {issue.automationName}:
                      </span>{" "}
                      {issue.message}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground">
                  You can still import them, but they may need editing to run.
                </p>
              </div>
            )}
            {unresolved.length > 0 && (
              <div className="flex flex-col gap-2 rounded-md border p-3">
                <p className="text-sm font-medium">Resolve references</p>
                <p className="text-xs text-muted-foreground">
                  These automations reference entities from another shop. Map
                  each one to an entity in this shop, or leave it blank to skip
                  the automations that use it.
                </p>
                {!optionsReady ? (
                  <p className="text-xs text-muted-foreground">
                    Loading this shop&apos;s entities…
                  </p>
                ) : (
                  <>
                    {unresolved.map((entity) => (
                      <div
                        key={entity.key}
                        className="flex items-center justify-between gap-2"
                      >
                        <div className="flex min-w-0 flex-col">
                          <span className="truncate text-sm">
                            {entity.sourceLabel}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {DYNAMIC_TYPE_LABELS[entity.dynamicType] ??
                              entity.dynamicType}
                          </span>
                        </div>
                        <div className="w-48 shrink-0">
                          <Select
                            placeholder="Choose…"
                            value={mappings[entity.key]}
                            options={
                              generateOptions(undefined, entity.dynamicType) ??
                              []
                            }
                            onValueChange={(value) =>
                              setMappings((prev) => ({
                                ...prev,
                                [entity.key]: value,
                              }))
                            }
                          />
                        </div>
                      </div>
                    ))}
                    <p className="text-xs text-muted-foreground">
                      {mappedReferences} of {totalReferences} references mapped
                      {skippedCount > 0 &&
                        ` — ${skippedCount} automation${
                          skippedCount === 1 ? "" : "s"
                        } will be skipped`}
                    </p>
                  </>
                )}
              </div>
            )}
            <div className="flex flex-col gap-1">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="automation-import-mode"
                  checked={mode === "add"}
                  onChange={() => setMode("add")}
                />
                Add to current list
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="automation-import-mode"
                  checked={mode === "replaceAll"}
                  onChange={() => setMode("replaceAll")}
                />
                Replace all automations
              </label>
              {mode === "replaceAll" && automations.length > 0 && (
                <p className="text-xs text-destructive">
                  This will delete all {automations.length} current automation
                  {automations.length === 1 ? "" : "s"}.
                </p>
              )}
            </div>
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {plan.actions.map((action, index) => (
                <div key={index} className="rounded-md border p-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="min-w-0 truncate text-sm">
                      {action.automation.name || "Unnamed automation"}
                    </span>
                    {action.kind === "create" && <Badge>Create</Badge>}
                    {action.kind === "update" && (
                      <Badge variant={"secondary"}>Update</Badge>
                    )}
                    {action.kind === "reject" && (
                      <Badge variant={"destructive"}>Rejected</Badge>
                    )}
                  </div>
                  {action.kind === "update" && (
                    <p className="text-xs text-muted-foreground">
                      Updates &quot;
                      {automations.find((a) => a.id === action.id)?.name ??
                        action.id}
                      &quot;
                    </p>
                  )}
                  {action.kind === "reject" && (
                    <ul className="list-disc pl-5 text-xs text-destructive">
                      {action.reasons.map((reason, i) => (
                        <li key={i}>{reason}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
            <DialogFooter className="sm:justify-between">
              <Button
                variant={"outline"}
                onClick={() => {
                  setParsedFile(null);
                  setMappings({});
                  setStep("upload");
                }}
                disabled={saving}
              >
                Back
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={saving || writeCount === 0 || !optionsReady}
              >
                Import
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
